import {
  AppAlert,
  AppButton,
  AppCard,
  AppCheckbox,
  AppCombobox,
  AppDataTable,
  AppInput,
  AppTextarea,
} from '@9b3kuq5phwuk4txw/kiban-react-ui'
import type { OpportunityDetail } from 'backend/types'
import { DATE, FIELD_MAX_LENGTHS, INFORMATION_SOURCE_OPTIONS } from 'shared'
import { OpportunityStatusSelect } from '../OpportunityStatusSelect'
import { derivedProjectColumns } from './components/columns'
import { usePropertiesTab } from './hooks/usePropertiesTab'

interface PropertiesTabProps {
  opportunity: OpportunityDetail
  editable: boolean
}

export function PropertiesTab({ opportunity, editable }: PropertiesTabProps) {
  const {
    // フォーム状態 + ハンドラ
    title,
    handleTitleChange,
    informationSource,
    handleInformationSourceChange,
    description,
    handleDescriptionChange,
    isExpansion,
    setIsExpansion,
    startOn,
    setStartOn,
    contactName,
    handleContactNameChange,
    // ステータス関連
    statusState,
    handleStatusChange,
    handleCloseReasonChange,
    handleAdditionalCloseReasonChange,
    handleGCompanyNameChange,
    handleGCompanyContactChange,
    // バリデーション
    errors,
    hasErrors,
    // 派生データ
    statuses,
    isSystemManaged,
    groupedCloseReasons,
    isStatusDataLoading,
    currentStatus,
    isClosed,
    isGCompanyFlow,
    isAdditionalCloseReasonRequired,
    // ミューテーション
    handleSubmit,
    isPending,
    // フォーカス
    focusedField,
    setFocusedField,
  } = usePropertiesTab(opportunity)

  return (
    <div className='p-4 h-full overflow-auto'>
      <div className='space-y-6'>
        {/* アラート */}
        {editable && (
          <>
            <AppAlert type='error'>機密情報にあたる文言入力はお控えいただけますようご留意ください</AppAlert>
            <AppAlert type='warning'>
              「SQL登録完了」はSalesforceの情報をもとに自動更新されます。
              SQL化する場合は売上情報にオポ管理IDを紐付け、「Open」のままお待ちください。
            </AppAlert>
          </>
        )}

        {/* 派生案件テーブル */}
        {opportunity.to_projects && opportunity.to_projects.length > 0 && (
          <AppCard title='オポチュニティから派生した案件の情報' className='gap-2 py-6'>
            <AppDataTable
              data={opportunity.to_projects}
              columns={derivedProjectColumns}
              getRowKey={(row, index) => `${row.ano}-${index}`}
              paginated={false}
            />
          </AppCard>
        )}

        {/* 必須項目 */}
        <div>
          <h3 className='text-lg font-medium py-3'>必須項目</h3>
          <div className='space-y-4'>
            {/* タイトル */}
            <AppInput
              id='title'
              label='タイトル'
              value={title}
              onChange={handleTitleChange}
              disabled={!editable}
              maxLength={FIELD_MAX_LENGTHS.OPPORTUNITY_TITLE}
              showCharCount={editable}
              placeholder='タイトルを入力'
              error={errors.title}
            />

            {/* 紐付け案件（読み取り専用） */}
            <AppInput
              label='オポチュニティを紐づける案件'
              value={opportunity.salesforce_project_name || '未設定'}
              disabled
              readOnly
            />

            {/* 拡張案件チェック */}
            <AppCheckbox
              label='上記案件の拡張案件に該当する'
              checked={isExpansion}
              onCheckedChange={(checked) => setIsExpansion(checked === true)}
              disabled={!editable}
            />
          </div>
        </div>

        {/* 補足項目 */}
        <div>
          <h3 className='text-lg font-medium py-3'>補足項目</h3>
          <div className='space-y-4'>
            {/* 情報源 */}
            <AppCombobox
              value={informationSource}
              onValueChange={handleInformationSourceChange}
              options={INFORMATION_SOURCE_OPTIONS.map((opt) => ({ value: opt, label: opt }))}
              disabled={!editable}
              field={{
                label: 'オポチュニティの情報源',
                error: errors.informationSource,
                helpText: 'この情報の入手元を選択もしくは自由入力してください',
              }}
              freeInput={{
                maxLength: FIELD_MAX_LENGTHS.OPPORTUNITY_INFORMATION_SOURCE,
                showCharCount: editable,
              }}
            />

            {/* 詳細 */}
            <AppTextarea
              id='description'
              label='オポチュニティの詳細'
              value={description}
              onChange={handleDescriptionChange}
              onFocus={() => setFocusedField('description')}
              onBlur={() => setFocusedField(null)}
              disabled={!editable}
              placeholder='詳細な説明を入力'
              rows={5}
              maxLength={FIELD_MAX_LENGTHS.OPPORTUNITY_DESCRIPTION}
              helpText={
                editable && focusedField === 'description'
                  ? 'お客様の課題／お困り事をなるべく具体的に、お困り事の背景はわかる範囲で記載してください'
                  : undefined
              }
              showCharCount
              limitHeight='xl'
              error={errors.description}
            />

            {/* 開始予定日、取引先責任者 */}
            <div className='grid grid-cols-1 md:grid-cols-2 gap-4'>
              {/* 開始予定日(入力日の1年前から1年後の日付まで選択可能) */}
              <AppInput
                id='startOn'
                label='案件の開始予定日'
                type='date'
                value={startOn}
                onChange={(e) => setStartOn(e.target.value)}
                disabled={!editable}
                min={DATE.oneYearAgo}
                max={DATE.oneYearLater}
              />

              {/* 取引先責任者 */}
              <AppInput
                id='contactName'
                label='取引先責任者'
                value={contactName}
                onChange={handleContactNameChange}
                disabled={!editable}
                maxLength={FIELD_MAX_LENGTHS.OPPORTUNITY_CONTACT_NAME}
                showCharCount={editable}
                placeholder='取引先責任者名を入力'
                error={errors.contactName}
              />
            </div>

            {/* ステータス選択 */}
            <OpportunityStatusSelect
              editable={editable}
              isPending={isPending}
              statusState={statusState}
              onStatusChange={handleStatusChange}
              onCloseReasonChange={handleCloseReasonChange}
              onAdditionalCloseReasonChange={handleAdditionalCloseReasonChange}
              onGCompanyNameChange={handleGCompanyNameChange}
              onGCompanyContactChange={handleGCompanyContactChange}
              statuses={statuses}
              isSystemManaged={isSystemManaged}
              groupedCloseReasons={groupedCloseReasons}
              isClosed={isClosed}
              isGCompanyFlow={isGCompanyFlow}
              isAdditionalCloseReasonRequired={isAdditionalCloseReasonRequired}
              hintText={currentStatus?.hint || ''}
              errors={errors}
              isLoading={isStatusDataLoading}
            />
          </div>
        </div>

        {/* 更新ボタン */}
        {editable && (
          <div className='flex justify-end'>
            <AppButton onClick={handleSubmit} disabled={isPending || hasErrors} className='min-w-37.5'>
              {isPending ? '更新中...' : '更新'}
            </AppButton>
          </div>
        )}
      </div>
    </div>
  )
}
